import { Bike, Mountain, RefreshCw, Settings, SlidersHorizontal, Trash, Trash2 } from 'lucide-react';
import { LockedVisit } from 'bindings/optimization/LockedVisit';
import { PointConfig } from 'bindings/optimization/PointConfig';
import { TSPHotelNight } from 'bindings/optimization/TSPHotelNight';
import { TSPRoutingConfig } from 'bindings/optimization/TSPRoutingConfig';
import OptimizeIMG from 'images/icons/ai_line.svg';
import * as moment from 'moment';
import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AFormTemplate } from '../../../atoms/forms';
import { AProfile, useMe } from '../../../atoms/global/users';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import Input from '../../../components_v2/input/Input';
import InputAddress from '../../../components_v2/input/InputAddress';
import { InputAddressResult } from '../../../components_v2/input/model/Model';
import { PanelContext } from '../../../components_v2/panel/context';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import Popup from '../../../components_v2/popup/Popup';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import Splitter from '../../../components_v2/splitter/Splitter';
import { isSuperAdmin, useWindowDimensions } from '../../../components_v2/utils';
import RestrictedSuperAdmin from '../../../containers_v2/permissions/RestrictedSuperAdmin';
import { getLocalStorage } from '../../../helpers/localStorage';
import storeLang from '../../../helpers/storeLang';
import CarIcon from '../../../styles/custom-icons/car';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { Loader } from '../../../styles/global/css/GlobalLoader';
import { PrimaryColor, BorderColor, DarkGreySidely2, OrangeSidely, RedSidely, SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';
import LoaderContext, { LoaderState } from '../../../styles/global/loader/LoaderContext';
import { Translate, translateToString } from '../../../styles/global/translate';
import { useLocalStorageState } from '../../../utils/customHooks';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert from '../../alert/UseAlert';
import { NoMarginH5, NoMarginH6, NoMarginP } from '../../authorization/users/Members';
import SidelyCalendar, { CalendarEvent, CalendarPeriod, EventsTypes, toTemporaryEvents } from '../../calendar/Calendar';
import { HoveredEvent } from '../../calendar/CalendarView';
import { modifyEvent, WEEK_DAY_LIST } from '../../calendar/EventsApi';
import { colorTypeMapping } from '../../calendar/model/Model';
import { getRawCompanies } from '../../client-companies/data/action';
import { SortAndDots } from '../../client-companies/popup/CompanyListingPopup';
import { LoadingStateEnum } from '../../import/model';
import { FlexDiv } from '../../products/style';
import { BoolSlider } from '../../settings/subSettings/CalculatedFieldsCreation';
import { UserWithAdditionalFields } from '../../settings/userSettings/myProfile';
import { CalendarMapView, useTripFilter } from '../calendarMapView';
import { NullableReactStateType } from '../Map';
import { MAP_DEFAULT_PIN_SIZE, MapDotType, MapPinWithImage } from '../MapPin';
import { bulkCreateEvents, sequenceEvents, sequenceEventsTSP, TSPResponse } from './actions';
import AdvancedSettingsConstraint, { genDefaultAdvancedSettings } from './constraints/advancedSettings';
import Constraint from './constraints/constraints';
import { getWorkingDaysAmount } from './constraints/workingDays';
import { Duration, durationToSeconds, SimpleTimePicker } from './createEventFromLasso';
import { Interconnection, LoadingState, MapEventOptions, OptimizeCompany, TSP_COMPANIES_LIMIT } from './model';
import { eventsToLockedVisits } from './replayState';
import { ButtonContainer, ModalNoClose, TopContainer } from './style';
import { SIDEBAR_CLOSE_WIDTH } from '../../../components_v2/sidebar/style';
import type { TSPBody } from 'bindings/optimization/TSPBody';
import { canUserAccess } from '../../../atoms/allowDenyList';
import { UserDoodle } from '../../../styles/custom-icons/doodles/doodle';

export function inputAddressResultToString(value?: InputAddressResult): string | undefined {
	if (value == null) return;
	return `${value.street_number} ${value.route}, ${value.city}, ${value.country}`;
};

const InputContainer = styled.div`
	display: flex;
	flex-flow: column;
	height: fit-content;
	font-size: 12px;
	color: ${DarkGreySidely2};
	width: 100%;
`;

const DeletePauseDiv = styled.div<{ hoverColor?: string }>`
	cursor: pointer;
	width: 100%;
	border-radius: 5px;
	padding: 5px;
  	display: flex;
   	align-items: center;
    gap: 5px;
    font-size: 12px;
    color: ${DarkGreySidely2};
    & > svg {
			color: ${DarkGreySidely2};
	}
	&:hover {
		color: ${p => p.hoverColor ?? RedSidely};
		background-color: color-mix(in oklab, ${SidelyBlack} 3%, transparent);
		& > svg {
			color: ${p => p.hoverColor ?? RedSidely};
		}
	}
`;

export type OptimizationInformations = {
	start: Date,
	end: Date,
	pause?: {
		time: Date,
		duration_sec: number
	}
}

export const initWaypoint = (value: string, profile: UserWithAdditionalFields): InputAddressResult | undefined => {
	// User-saved overrides win. Profile address is only the DEFAULT — used when
	// the user hasn't picked anything yet. Previously this function unconditionally
	// overwrote storage with the profile fields whenever
	// `use_address_as_default_optimization_start` was enabled, which silently
	// ignored any UI override of the home address.
	const storage: InputAddressResult | undefined = getLocalStorage(value);
	if (storage) return storage;

	const useProfileAsDefault = (value === 'origin' && profile.use_address_as_default_optimization_start)
		|| (value === 'destination' && profile.use_address_as_default_optimization_end);
	if (useProfileAsDefault && profile.latitude !== undefined && profile.longitude !== undefined) {
		return {
			city: profile.city ?? '',
			country: profile.country ?? '',
			street_number: profile.address ?? '',
			route: '',
			postcode: profile.postal_code ?? '',
			longitude: profile.longitude,
			latitude: profile.latitude,
		} as InputAddressResult;
	}
	return undefined;
};

function jsDateToNaiveDate(date: Date | moment.Moment): string {
	// Local-time YYYY-MM-DD. toISOString() returns UTC, which silently shifts
	// the calendar day back by one for any Date at midnight in a positive-offset
	// timezone (e.g. react-big-calendar passes "Thursday midnight local" for the
	// day header — that's the previous day in UTC for any timezone east of London).
	return moment(date).format('YYYY-MM-DD');
}

type ConstraintInnerType<T> = {
	settings: T,
	label: string
}

type HotelNight = {
	date: string,
	address?: InputAddressResult
}

export type HotelNightConstraint = {
	nights: HotelNight[],
	allNights: boolean
}

type WorkingDaysConstraint = {
	startDate?: moment.Moment,
	endDate?: moment.Moment,
	workdays: number[],
	firstEvent: Date
}

export type ConstraintType = {
	VisitDuration: ConstraintInnerType<{ default: Duration, field_id: number | undefined }>,
	OpeningTimes: ConstraintInnerType<Set<number>>,
	PlanifiedEvents: ConstraintInnerType<{ events: Set<number>, allowOvertime: boolean }>,
	HotelNights: ConstraintInnerType<HotelNightConstraint>,
	WorkingDays: ConstraintInnerType<WorkingDaysConstraint>
}

export type Constraints = keyof ConstraintType;

type PlannedEvent = CalendarEvent & { isInOptimization?: boolean };

const MAX_OLD_ALGORITHM = 30;
const MAX_NEW_ALGORITHM = 9999;

const LOCKED_VISIT_PRIORITY = 999999;
// Replay ignores the end-of-day constraint: the tuned day may run until late
// evening so an added visit extends the route instead of bumping anything.
const REPLAY_EXTENDED_DAY_END = 23 * 3600 + 30 * 60;
const DEFAULT_PRIORITY = 10;

/** Every geocoded company of the account: the pool when no subset was chosen, and the fill-up pool. */
function fetchAllGeocodedCompanies(): Promise<OptimizeCompany[]> {
	return getRawCompanies({
		offset: 0,
		limit: TSP_COMPANIES_LIMIT,
		columns: ['name', 'latitude', 'longitude'],
		new_filters: {
			and: [
				{ val: { operator: 'not_empty', column: 'latitude', filter_type: 'numeric' } },
				{ val: { operator: 'not_empty', column: 'longitude', filter_type: 'numeric' } }
			]
		},
		order_bys: undefined
	}).then(res => res.row.map((c) => ({ id: c.companyId!, lat: c.latitude!, lng: c.longitude!, name: c.name! })));
}

function secondsSinceMidnight(date: Date): number {
	// Create a copy and set it to 00:00:00:000
	const midnight = new Date(date);
	midnight.setHours(0, 0, 0, 0);

	// Difference is in milliseconds, so divide by 1000
	return Math.floor((date.getTime() - midnight.getTime()) / 1000);
}

export function OptimizePopup(props: {
	samePriority?: boolean,
	rawCompanies?: { id: number, name?: string }[],
	companies?: OptimizeCompany[],
	errorCompanies: { id: number, lat: number, lon: number }[],
	onOptimize: (ids: string[], interconnections: Interconnection[], optimizationInformations: OptimizationInformations | undefined) => void
	isOpen: boolean
	setIsOpen: (b: boolean) => void,
	allInformations?: boolean,
	hourStartDay?: Date,
	hourEndDay?: Date,
	pauseHour?: Date,
	visitDuration?: Duration,
	pauseDuration?: number,
	daysSelected?: number[],
	firstEventDate?: Date,
	noPopup?: boolean,
	onTSPOptimize?: (res: TSPResponse, points: PointConfig[], hotelNights: HotelNightConstraint | undefined, workingDays: WorkingDaysConstraint | undefined, additionalCompanies: OptimizeCompany[], body: TSPBody) => void
	plannedEvents: PlannedEvent[],
	mainPageConstraints?: Set<Constraints>
	defaultConstraints?: Set<Constraints>
	alwaysTsp?: boolean
	onGenerateEvents?: () => void
	formId?: number
	originHook?: NullableReactStateType<InputAddressResult>
	children?: React.ReactNode,
	maxVisitsPerDay?: number
	maxDailyKm?: number
	/** Offers "fill up the week": the solver keeps every generated visit on
	 * its day (slot and order free) and completes the days from the whole
	 * account, whatever subset the first pass ran on. */
	canFillUp?: boolean
}) {
	const [hourStartDay, setHourStartDay] = useLocalStorageState<Date, number>('optimization_hour_start_day', new Date(2000, 1, 1, 8, 0), (d) => d?.getHours(), (h) => new Date(2000, 1, 1, h ?? 8, 0));
	const [hourEndDay, setHourEndDay] = useLocalStorageState('optimization_hour_end_day', new Date(2000, 1, 1, 17, 0), (d) => d?.getHours(), (h) => new Date(2000, 1, 1, h ?? 17, 0));
	const [pauseHour, setPauseHour] = useLocalStorageState<Date | undefined, number | undefined>('optimization_pause_hour', undefined, (d) => d?.getHours(), (h) => h ? new Date(2000, 1, 1, h, 0) : undefined);
	const [pauseEndHour, setPauseEndHour] = useLocalStorageState<Date | undefined, number | undefined>('optimization_pause_end_hour', undefined, (d) => d?.getHours(), (h) => h ? new Date(2000, 1, 1, h, 0) : undefined);

	const profile = useRecoilValue(AProfile);
	let companies = props.companies ?? [];
	const { onOptimize, isOpen, setIsOpen, onTSPOptimize } = props;
	const [origin, setOrigin] = props.originHook ?? React.useState<InputAddressResult | undefined>(initWaypoint('origin', profile));
	const [destination, setDestination] = React.useState<InputAddressResult | undefined>(initWaypoint('destination', profile));
	const [transportMode, setTransportMode] = useLocalStorageState('transportation-mode', 'car');
	const { loaderNotify } = React.useContext(LoaderContext);
	const translate = getTranslate(storeLang.getState().localize);
	const alert = useAlert();
	const [tspResponse, setTspResponse] = React.useState<TSPResponse>();
	// The pool `tspResponse` was solved on: node indices map onto it, not
	// onto `props.companies`, which a fill-up leaves narrower than the solve.
	const [solvedPool, setSolvedPool] = React.useState<OptimizeCompany[]>();
	// Compare mode: one entry per strategy, `choose` applies that plan.
	const [compareResults, setCompareResults] = React.useState<{ strategy: string, res: TSPResponse, choose:() => void }[]>();
	const [isTsp, setIsTsp] = React.useState<boolean>(props.alwaysTsp ?? (props.companies?.length ?? 0) > MAX_OLD_ALGORITHM);
	const advancedSettingsHook = React.useState<TSPRoutingConfig>(genDefaultAdvancedSettings());
	const isAdmin = isSuperAdmin();

	const CONSTRAINTS: ConstraintType = {
		OpeningTimes: { label: translateToString('temporal_constrains'), settings: new Set(getLocalStorage<number[]>('OpeningTimes')) },
		VisitDuration: { label: translateToString('visit_duration'), settings: { default: props.visitDuration ?? getLocalStorage<Duration, number>('VisitDuration_default', (nb) => ({ op: 'min', nb: nb?.toString() ?? '45' })) ?? { op: 'min', nb: '45' }, field_id: getLocalStorage('VisitDuration_field_id') } },
		PlanifiedEvents: { label: translateToString('planned_events'), settings: { events: new Set(getLocalStorage('PlanifiedEvents_events') ?? [1, 2, 3, 4, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]), allowOvertime: getLocalStorage('PlanifiedEvents_allowOvertime') ?? true } },
		HotelNights: { label: translateToString('hotel_nights'), settings: { nights: [], allNights: true } },
		WorkingDays: {
			label: translateToString('map.modal_right.days_of_activity'), settings: {
				startDate: moment(props.firstEventDate),
				endDate: moment(props.firstEventDate).endOf('week'),
				workdays: props.daysSelected ?? getLocalStorage('WorkingDays_workdays') ?? [0, 1, 2, 3, 4],
				firstEvent: props.hourStartDay ?? hourStartDay
			}
		}
	};
	const constraintsHook = React.useState<Map<Constraints, unknown | undefined>>(new Map([...props.mainPageConstraints ?? [], ...props.defaultConstraints ?? []]?.map(c => [c, CONSTRAINTS[c].settings])));
	const [constraints, setConstraints] = constraintsHook;
	const [constraintsSettingsOpen, setConstraintsSettingsOpen] = React.useState<Constraints | 'advanced_settings'>();


	const setAdressLocalStorage = (value: InputAddressResult, key: string) => {
		localStorage.setItem(key, JSON.stringify(value));
		switch (key) {
			case 'origin':
				setOrigin(value);
				break;
			case 'destination':
				setDestination(value);
				break;
		}
	};

	const TSP_STRATEGIES = ['visits', 'min-driving', 'balanced', 'finish-early', 'priority-first', 'sectors'];
	const TSP_COMPARE_STRATEGIES = ['visits', 'min-driving', 'balanced'];
	const transportationModes: DropdownData[] = [
		{ label: translate('car') as string, value: 'car', imageJsx: <CarIcon /> },
		{ label: translate('pedestrian') as string, value: 'pedestrian', imageJsx: <Mountain /> },
		// { label: translate('truck') as string, value: 'truck' },
		{ label: translate('bicycle') as string, value: 'bicycle', imageJsx: <Bike /> }
	];

	const optimise = async(compareStrategies?: string[], fillUp?: boolean) => {
		// Old version
		if (!isTsp) {
			loaderNotify('global', LoadingStateEnum.LOADING);
			sequenceEvents({
				waypoints: [
					...companies,
					{ id: -1, lat: destination?.latitude, lng: destination?.longitude },
					{ id: -2, lat: origin?.latitude, lng: origin?.longitude }
				],
				mode: transportMode
			})
				.then(res => {
					onOptimize([...res.data.waypoints.filter(id => id > 0), ...props.errorCompanies.map(c => c.id.toString())], res.data.interconnections, props.allInformations ? {
						start: hourStartDay,
						end: hourEndDay,
						pause: pauseHour && pauseEndHour ? {
							time: pauseHour,
							duration_sec: (pauseEndHour.getTime() - pauseHour.getTime()) / 1000
						} : undefined
					} : undefined);
					loaderNotify('global', LoadingStateEnum.LOADED);
				})
				.catch(e => {
					console.error(e);
					toast.error(translateToString('tsp_error_unable_to_sequence'));
					loaderNotify('global', LoadingStateEnum.LOADED);
				});
			setIsOpen(!isOpen);
		} else {
			// Refuse the launch instead of sending a day, a pause, a period or a
			// visit of negative length: the solver answers with an empty plan.
			const dayStartSecs = secondsSinceMidnight(props.hourStartDay ?? hourStartDay);
			const dayEndSecs = secondsSinceMidnight(props.hourEndDay ?? hourEndDay);
			if (dayEndSecs <= dayStartSecs) {
				toast.error(translateToString('tsp_error_day_hours'));
				return;
			}
			const pauseStart = props.pauseHour ?? pauseHour;
			const pauseEnd = pauseStart && (pauseEndHour ?? (props.pauseDuration ? new Date(pauseStart.getTime() + props.pauseDuration) : undefined));
			if (pauseStart && pauseEnd) {
				const pauseStartSecs = secondsSinceMidnight(pauseStart);
				const pauseEndSecs = secondsSinceMidnight(pauseEnd);
				if (pauseEndSecs <= pauseStartSecs) {
					toast.error(translateToString('tsp_error_pause_hours'));
					return;
				}
				if (pauseStartSecs < dayStartSecs || pauseEndSecs > dayEndSecs) {
					toast.error(translateToString('map.modal_right.pause_out_of_work_day'));
					return;
				}
			}
			if (getWorkingDaysAmount(constraints.get('WorkingDays') as ConstraintType['WorkingDays']['settings'] | undefined) <= 0) {
				toast.error(translateToString('tsp_error_no_working_days'));
				return;
			}
			const visitSecs = durationToSeconds((constraints.get('VisitDuration') as ConstraintType['VisitDuration']['settings'] | undefined)?.default ?? props.visitDuration);
			if (!(visitSecs > 0)) {
				toast.error(translateToString('tsp_error_visit_duration'));
				return;
			}
			loaderNotify('global', LoadingStateEnum.LOADING);
			// The fill-up completes around the pinned visits with the whole
			// account — a lasso or a filter only shapes the first pass; the
			// server-side candidate pre-filter keeps the nearest companies.
			if (fillUp || companies.length === 0) companies = await fetchAllGeocodedCompanies();
			const maxLen = companies.length;
			// Priority assignment:
			//   - samePriority (no explicit ordering): every point gets DEFAULT_PRIORITY,
			//     filtering alone never implies rank.
			//   - Otherwise (ordering is active): bucket the sorted positions into
			//     N_PRIORITY_LEVELS bands. Contiguous chunks of the list share the same
			//     priority, which approximates "same sort-group value = same priority".
			//     True per-group priority would need the backend to expose sort-group
			//     ranks; this heuristic is much better than the old unique-per-index
			//     scheme where 500 points produced 500 distinct priorities the solver
			//     wasted weight on.
			const N_PRIORITY_LEVELS = 10;
			const bucketPriority = (index: number): number => {
				if (maxLen <= 0) return 1;
				const bucket = Math.floor((index * N_PRIORITY_LEVELS) / maxLen);
				return N_PRIORITY_LEVELS - bucket; // index 0 → top level, last index → 1
			};
			const points: PointConfig[] = companies.map((c, index) => (({
				latitude: c.lat,
				longitude: c.lng,
				id: c.id,
				node_duration: null,
				// the fill-up pool carries no ordering: only the pins rank
				priority: props.samePriority || fillUp ? DEFAULT_PRIORITY : bucketPriority(index)
			})));
			if (origin) {
				points.unshift({
					latitude: origin.latitude,
					longitude: origin.longitude,
					id: null,
					node_duration: 0,
					priority: LOCKED_VISIT_PRIORITY
				});
			}
			let first_visit_day = moment(props.firstEventDate).day() - 1;
			if (first_visit_day === -1) first_visit_day = 6;
			// Fill-up pins: every generated visit of the previous pass keeps
			// its day but not its slot — a locked visit whose window is the
			// whole working day and whose duration is the visit's own, so the
			// solver re-times and re-orders the day around the newcomers.
			// Companies missing from the pool are appended with their id so
			// they come back as calendar events.
			const fillUpLocks: LockedVisit[] = [];
			const fillUpIds = new Set<number>();
			if (fillUp) {
				const dayStart = secondsSinceMidnight(props.hourStartDay ?? hourStartDay);
				const dayEnd = secondsSinceMidnight(props.hourEndDay ?? hourEndDay);
				for (const event of props.plannedEvents) {
					if (event.type !== EventsTypes.TemporaryEvent || event.clientCompanyId == null) continue;
					const duration = Math.round((event.end.getTime() - event.start.getTime()) / 1000);
					if (duration <= 0) continue;
					let node_index = points.findIndex(p => p.id === event.clientCompanyId);
					if (node_index === -1) {
						if (!event.latitude || !event.longitude) continue;
						node_index = points.length;
						points.push({
							latitude: event.latitude,
							longitude: event.longitude,
							id: event.clientCompanyId,
							node_duration: null,
							priority: LOCKED_VISIT_PRIORITY,
						});
						companies.push({ id: event.clientCompanyId, lat: event.latitude, lng: event.longitude, name: event.companieName });
					} else {
						points[node_index].priority = LOCKED_VISIT_PRIORITY;
					}
					fillUpIds.add(event.clientCompanyId);
					fillUpLocks.push({
						node_index,
						day: jsDateToNaiveDate(event.start),
						visit_time: { start: dayStart, end: dayEnd },
						allow_overtime: false,
						duration,
					});
				}
			}
			const planifiedEvents = constraints.get('PlanifiedEvents') as ConstraintType['PlanifiedEvents']['settings'] | undefined;
			const locked_visits: LockedVisit[] = [];
			if (planifiedEvents && planifiedEvents.events.size > 0) {
				for (const event of props.plannedEvents.filter(e => e.scheduled)) {
					if (!planifiedEvents.events.has(event.eventTypeId) || !event.clientCompanyId || !event.latitude || !event.longitude) continue;
					let node_index = points.findIndex(c => c.id == event.clientCompanyId);
					if (node_index === -1) {
						node_index = points.length;
						points.push({
							latitude: event.latitude!,
							longitude: event.longitude!,
							id: null,
							node_duration: null,
							priority: LOCKED_VISIT_PRIORITY,
						});
					} else {
						points[node_index].id = null;
						points[node_index].priority = LOCKED_VISIT_PRIORITY;
					}
					locked_visits.push({
						node_index,
						visit_time: {
							start: secondsSinceMidnight(event.start),
							end: secondsSinceMidnight(event.end)
						},
						day: jsDateToNaiveDate(event.start),
						allow_overtime: planifiedEvents.allowOvertime
					});
				}
			}
			const hotelConstraints = constraints.get('HotelNights') as ConstraintType['HotelNights']['settings'] | undefined;
			const hotel_nights = hotelConstraints?.nights.reduce<Record<string, TSPHotelNight>>((acc, night) => {
				if (night.address) {
					acc[night.date] = { 'Node': points.length };
					points.push({
						latitude: night.address.latitude,
						longitude: night.address.longitude,
						id: null,
						node_duration: null,
						priority: LOCKED_VISIT_PRIORITY,
					});
				} else {
					acc[night.date] = 'NoNode';
				}
				return acc;
			}, {}) ?? {};
			const workingDaysConstraint = constraints.get('WorkingDays') as ConstraintType['WorkingDays']['settings'] | undefined;
			const visitDuration = constraints.get('VisitDuration') as ConstraintType['VisitDuration']['settings'] | undefined;
			const body: TSPBody = {
				points,
				max_days: getWorkingDaysAmount(workingDaysConstraint),
				duration_field_id: visitDuration?.field_id ?? null,
				openning_hours_field_id: [...(constraints.get('OpeningTimes') as ConstraintType['OpeningTimes']['settings'] | undefined) ?? []],
				working_hours: {
					start: secondsSinceMidnight(props.hourStartDay ?? hourStartDay)!,
					end: secondsSinceMidnight(props.hourEndDay ?? hourEndDay)!
				},
				pause: (props.pauseHour ?? pauseHour) && (props.pauseDuration ?? pauseEndHour) ? {
					start: secondsSinceMidnight(props.pauseHour ?? pauseHour!)!,
					end: secondsSinceMidnight(pauseEndHour ?? new Date((props.pauseHour ?? pauseHour!).getTime() + props.pauseDuration!))!
				} : null,
				transport_mode: transportMode == 'car' ? 'auto' : transportMode,
				visit_duration: durationToSeconds(visitDuration?.default ?? props.visitDuration),
				locked_visits,
				first_visit_hour: secondsSinceMidnight(workingDaysConstraint?.firstEvent ?? props.firstEventDate!),
				first_visit_day: jsDateToNaiveDate(workingDaysConstraint?.startDate ?? props.firstEventDate!),
				working_days: props.daysSelected?.map(i => WEEK_DAY_LIST[i]) ?? [...WEEK_DAY_LIST],
				hotel_nights,
				all_nights_in_hotels: hotelConstraints?.allNights ?? false,
				advanced_settings: { ...advancedSettingsHook[0], max_daily_km: props.maxDailyKm },
				max_visits_per_day: props.maxVisitsPerDay ?? null
			};
			const requests: Promise<void> = compareStrategies
				? Promise.all(compareStrategies.map(strategy => {
					const sBody: TSPBody = { ...body, advanced_settings: { ...body.advanced_settings, strategy } };
					return sequenceEventsTSP(sBody).then(res => ({
						strategy,
						res,
						choose: () => {
							setCompareResults(undefined);
							setTspResponse(res);
							setSolvedPool(companies);
							onTSPOptimize?.(res, points, hotelConstraints, workingDaysConstraint, companies, sBody);
						},
					}));
				})).then(results => {
					loaderNotify('global', LoadingStateEnum.LOADED);
					setCompareResults(results);
				})
				: sequenceEventsTSP(fillUp ? { ...body, locked_visits: [...locked_visits, ...fillUpLocks] } : body).then(res => {
					loaderNotify('global', LoadingStateEnum.LOADED);
					setTspResponse(res);
					setSolvedPool(companies);
					// `body` keeps only the real appointment locks: the per-day
					// replay derives tight locks from the calendar itself.
					onTSPOptimize?.(res, points, hotelConstraints, workingDaysConstraint, companies, body);
					if (fillUpIds.size === 0) return;
					const servedIds = new Set(res.routes.flatMap(r => r.stops.map(s => points[s.node]?.id)));
					const lost = [...fillUpIds].filter(id => !servedIds.has(id));
					if (lost.length === 0) return;
					const names = lost.map(id => companies.find(c => c.id === id)?.name).filter((n): n is string => !!n);
					toast.warn(translateToString('tsp_visits_not_kept', [['count', String(lost.length)], ['names', `${names.slice(0, 4).join(', ')}${names.length > 4 ? '…' : ''}`]]));
				});
			requests
				.catch(e => {
					console.error(e);
					toast.error(translateToString('tsp_error_unable_to_sequence'));
					loaderNotify('global', LoadingStateEnum.LOADED);
				});
		}
	};

	const launchOptimization = (strategies?: string[], fillUp?: boolean) => {
		if (props.errorCompanies.length > 0) {
			alert({
				zIndex: 1052,
				title: 'Attention !',
				content: <Translate id='optimisation_ignored' />,
			}).then(res => {
				if (res.res == AlertRes.Ok) optimise(strategies, fillUp);
			});
		} else
			optimise(strategies, fillUp);
	};

	let content: React.ReactNode;
	let buttons: React.ReactNode;
	let gap: string | undefined = undefined;

	if (constraintsSettingsOpen) {
		gap = '15px';
		if (constraintsSettingsOpen === 'advanced_settings') content = <AdvancedSettingsConstraint advancedSettingsHook={advancedSettingsHook} />;
		else content = <Constraint constraintsHook={constraintsHook} defaultConstraints={CONSTRAINTS} constraint={constraintsSettingsOpen} />;
		buttons = <DefaultButton
			margin='0px'
			width='150px'
			buttonStyle={ButtonStyle.White}
			onClick={() => setConstraintsSettingsOpen(undefined)}
		>
			<Translate id='back' />
		</DefaultButton>;
	} else if (!tspResponse && compareResults) {
		content = <>
			<ModalCalendarH4><Translate id='tsp_compare_title' /></ModalCalendarH4>
			{compareResults.map(({ strategy, res, choose }) => {
				const visits = res.total?.visits ?? res.routes.reduce((acc, r) => acc + r.stops.length, 0);
				return <CompareCard key={strategy}>
					<CompareCardTitle><Translate id={`tsp_strategy_${strategy.replace(/-/g, '_')}`} /></CompareCardTitle>
					<CompareCardMetrics>
						{visits} <Translate id='tsp_travel_visits' /> · {formatTravelTime(res.total?.travel_secs ?? 0)} · {formatTravelKm(res.total?.travel_km ?? 0)}
					</CompareCardMetrics>
					<CompareCardMetrics muted>
						<Translate id='dropped_nodes' />: {res.dropped_nodes.length}
					</CompareCardMetrics>
					<DefaultButton
						margin='0px'
						height='36px'
						buttonStyle={ButtonStyle.IA}
						onClick={choose}
					>
						<Translate id='tsp_choose' />
					</DefaultButton>
				</CompareCard>;
			})}
		</>;
		buttons = <DefaultButton
			width='100%'
			margin='0'
			buttonStyle={ButtonStyle.White}
			onClick={() => setCompareResults(undefined)}
		>
			<Translate id='map.modal_right.cancel' />
		</DefaultButton>;
	} else if (!tspResponse) {
		const showSwitch = !props.alwaysTsp && (props.companies?.length ?? 0) <= MAX_OLD_ALGORITHM;
		const constraintsList: DropdownData<Constraints>[] = Object.keys(CONSTRAINTS).map(key => ({
			label: CONSTRAINTS[key].label,
			value: key as Constraints
		}));
		content = <>
			{showSwitch &&
				<RestrictedSuperAdmin>
					<FlexDiv gap='5px' width='100%'>
						<BoolSlider isActive={isTsp} onClick={() => setIsTsp(!isTsp)} />
						<ModalCalendarP><Translate id='advanced_algorithm' /></ModalCalendarP>
					</FlexDiv>
				</RestrictedSuperAdmin>
			}
			{isTsp && props.mainPageConstraints?.has('WorkingDays') && <Constraint constraintsHook={constraintsHook} defaultConstraints={CONSTRAINTS} constraint='WorkingDays' />}
			{props.allInformations && <>
				<FlexDiv gap='10px'>
					<InputContainer>
						<Translate id='start_day' />
						<SimpleTimePicker currDate={hourStartDay} onChange={setHourStartDay} />
					</InputContainer>
					<InputContainer>
						<Translate id='end_day' />
						<SimpleTimePicker currDate={hourEndDay} onChange={setHourEndDay} />
					</InputContainer>
				</FlexDiv>

				<FlexDiv gap='10px'>
					<InputContainer>
						<Translate id='start_pause' />
						<SimpleTimePicker currDate={pauseHour} onChange={setPauseHour} />
					</InputContainer>
					<InputContainer>
						<Translate id='end_pause' />
						<SimpleTimePicker currDate={pauseEndHour} onChange={setPauseEndHour} />
					</InputContainer>
				</FlexDiv>
				{(pauseHour || pauseEndHour) &&
					<DeletePauseDiv onClick={() => { setPauseEndHour(undefined); setPauseHour(undefined); }} >
						<Trash size={16} />
						<Translate id='delete_pause' />
					</DeletePauseDiv>
				}
			</>}
			<Dropdown
				required
				datalist={transportationModes}
				selectedValue={transportationModes.find(t => t.value == transportMode)}
				onChange={(value: DropdownData) => setTransportMode(value.value)}
				name='optimization-transportation-mode'

				dropdownStyle={{ width: '100%', labelUp: true, optionWidth: `${OPTIMIZATION_RIGHT_PANEL_WIDTH_WITHOUT_PADDING}px`, height: '35px', containerWidth: '100%', optionHeight: '200px', containerHeight: 'fit-content', }}
				label={translate('transportation_mode') as string}
			/>
			<InputAddress
				required
				name='optimization_origin'
				type='text'
				onChange={value => {
					setAdressLocalStorage(value, 'origin');
					// TSP mode has no separate destination input — the depot IS the
					// destination. Mirror the override to 'destination' too, otherwise
					// the calendar map's `getEventsConfig` keeps falling back to the
					// profile address (via use_address_as_default_optimization_end) and
					// renders the daily route ending at the profile location like a
					// hotel-night marker.
					if (isTsp) setAdressLocalStorage(value, 'destination');
				}}
				value={inputAddressResultToString(origin)}
				label={translate(isTsp ? 'start_address' : 'origin') as string}
				placeholder={translate(isTsp ? 'start_address' : 'origin') as string}
				optionWidth='250px'
				optionTop='55px'
				inputStyle={{ width: '100%', labelUp: true, containerWidth: '100%', containerHeight: 'fit-content' }}
			/>
			{props.children}
			{!isTsp && <InputAddress
				required
				name='optimization_destination'
				type='text'
				onChange={value => setAdressLocalStorage(value, 'destination')}
				value={inputAddressResultToString(destination)}
				label={translate('destination') as string}
				placeholder={translate('destination') as string}
				optionWidth='250px'
				optionTop='55px'
				inputStyle={{ width: '100%', labelUp: true, containerWidth: '100%', containerHeight: 'fit-content' }}
			/>}
			{isTsp && props.mainPageConstraints && [...props.mainPageConstraints].filter(c => c !== 'WorkingDays').map((c) => <Constraint key={c} constraintsHook={constraintsHook} defaultConstraints={CONSTRAINTS} constraint={c} />)}
			{isTsp && isAdmin && <ModalCalendarSettingsDescriptionAndDropdown>
				<ModalCalendarP><Translate id='tsp_strategy' /></ModalCalendarP>
				<Dropdown<string>
					dropdownStyle={{
						optionWidth: '100%',
						width: '100%',
						containerWidth: '100%',
					}}
					name='dropdown_tsp_strategy'
					readOnly
					searchable={false}
					datalist={TSP_STRATEGIES.map(s => ({ label: translateToString(`tsp_strategy_${s.replace(/-/g, '_')}`), value: s }))}
					selectedValue={{
						label: translateToString(`tsp_strategy_${(advancedSettingsHook[0].strategy ?? 'visits').replace(/-/g, '_')}`),
						value: advancedSettingsHook[0].strategy ?? 'visits',
					}}
					onChange={(v: DropdownData<string>) => advancedSettingsHook[1]({ ...advancedSettingsHook[0], strategy: v.value })}
				/>
			</ModalCalendarSettingsDescriptionAndDropdown>}
			{isTsp && <>
				<ModalCalendarH4><Translate id='optimization_constraints' /></ModalCalendarH4>
				<ModalCalendarDroppedNodeContainer>
					{[...constraints.keys()].map(constraintKey => {
						if (props.mainPageConstraints?.has(constraintKey)) return undefined;
						const constraint = CONSTRAINTS[constraintKey];
						if (!constraint) return undefined;
						return <DroppedNode key={constraintKey}>
							<NodeTitle>{constraint.label}</NodeTitle>
							<div style={{ display: 'flex', alignItems: 'center', gap: '0.5ch' }}>
								{constraint.settings && <Settings
									style={{ cursor: 'pointer' }}
									onClick={() => setConstraintsSettingsOpen(constraintKey)}
								/>}
								<Trash2
									style={{ cursor: 'pointer' }}
									color={RedSidely}
									onClick={() => setConstraints(s => {
										s.delete(constraintKey);
										return new Map(s);
									})}
								/>
							</div>
						</DroppedNode>;
					})}
					<Dropdown<Constraints>
						dropdownStyle={{
							optionHeight: '200px',
							optionWidth: `${OPTIMIZATION_RIGHT_PANEL_WIDTH_WITHOUT_PADDING}px`,
							width: '100%',
							containerWidth: '100%',
						}}
						JSXButton={() => <BlueTextA>
							<Translate id='add_constraints' />
						</BlueTextA>}
						searchable={false}
						disabled={false}
						readOnly
						name='dropdown_contraintes_tournee'
						datalist={constraintsList.filter(c => !constraints.has(c.value) && !props.mainPageConstraints?.has(c.value) && (isAdmin || c.value !== 'HotelNights'))}
						onChange={(value) => setConstraints(s => {
							s.set(value.value, CONSTRAINTS[value.value].settings);
							return new Map(s);
						})}
						autoOptionUp
					/>
				</ModalCalendarDroppedNodeContainer>
				<BlueTextA onClick={() => setConstraintsSettingsOpen('advanced_settings')}><SlidersHorizontal color={PrimaryColor} /><Translate id='advanced_settings' /></BlueTextA>
			</>}
		</>;

		buttons = <>
			<DefaultButton
				margin='0px'
				buttonStyle={ButtonStyle.White}
				onClick={() => setIsOpen(!isOpen)}
			>
				<Translate id='map.modal_right.cancel' />
			</DefaultButton>

			<DefaultButton
				margin='0px'
				buttonStyle={ButtonStyle.IA}
				disabled={!origin || (!isTsp && !destination)}
				onClick={() => launchOptimization()}
			>
				<Translate id='map.modal_right.launch_optimization' />
			</DefaultButton>
			{isTsp && isAdmin && <div style={{ gridColumn: 'span 2' }}>
				<DefaultButton
					margin='0px'
					width='100%'
					buttonStyle={ButtonStyle.White}
					disabled={!origin}
					onClick={() => launchOptimization(TSP_COMPARE_STRATEGIES)}
				>
					<Translate id='tsp_compare_strategies' />
				</DefaultButton>
			</div>}
		</>;
	} else {
		if (tspResponse.routes.length === 0) {
			content = <>
				<ModalCalendarH4><Translate id='optimization_failed' /></ModalCalendarH4>
				<ModalCalendarP><Translate id='optimization_failed_description' /></ModalCalendarP>
			</>;
			buttons = <DefaultButton
				width='100%'
				margin='0'
				onClick={() => setTspResponse(undefined)}
			>
				<Translate id='map.modal_right.cancel' />
			</DefaultButton>;
		} else {
			const [minDay, maxDay] = tspResponse.routes.reduce((acc, route) => [Math.min(acc[0], route.end_day), Math.max(acc[1], route.end_day + 1)], [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER]);
			// Map a solver day index (k-th working day) to a localised date,
			// mirroring the day placement used by applyTSPResponse.
			const summaryWorkingDays = constraints.get('WorkingDays') as ConstraintType['WorkingDays']['settings'] | undefined;
			const tspDayLabel = (dayIdx: number): string => {
				const workdays = summaryWorkingDays?.workdays;
				const date = moment(summaryWorkingDays?.startDate ?? props.firstEventDate);
				let remaining = dayIdx;
				for (let guard = 0; guard < 400; guard++) {
					let dow = date.day() - 1;
					if (dow === -1) dow = 6;
					if (!workdays || workdays.length === 0 || workdays.includes(dow)) {
						if (remaining === 0) break;
						remaining--;
					}
					date.add(1, 'day');
				}
				return date.format('ddd DD/MM');
			};
			const travelLine = (visits: number, secs: number, km: number) =>
				`${visits} · ${formatTravelTime(secs)} · ${formatTravelKm(km)}`;
			content = <>
				<ModalCalendarH4><Translate id='optimization_finished' options={{ variables: [['DAYS', (maxDay - minDay).toString()]] }} /></ModalCalendarH4>
				{(tspResponse.day_summaries?.length ?? 0) > 0 && <TravelSummaryContainer>
					<TravelSummaryHeader>
						<span><Translate id='tsp_travel_day' /></span>
						<span><Translate id='tsp_travel_visits' /> · <Translate id='tsp_travel_time' /> · km</span>
					</TravelSummaryHeader>
					{tspResponse.day_summaries?.map(day => <TravelSummaryRow key={day.day}>
						<span>{tspDayLabel(day.day)}</span>
						<span>{travelLine(day.visits, day.travel_secs, day.travel_km)}</span>
					</TravelSummaryRow>)}
					{(tspResponse.week_summaries?.length ?? 0) > 1 && tspResponse.week_summaries?.map(week => <TravelSummaryRow key={`week-${week.week}`} emphasis>
						<span><Translate id='week' /> {week.week}</span>
						<span>{travelLine(week.visits, week.travel_secs, week.travel_km)}</span>
					</TravelSummaryRow>)}
					{tspResponse.total && <TravelSummaryRow emphasis>
						<span><Translate id='tsp_travel_total' /></span>
						<span>{travelLine(tspResponse.total.visits, tspResponse.total.travel_secs, tspResponse.total.travel_km)}</span>
					</TravelSummaryRow>}
				</TravelSummaryContainer>}
				{tspResponse.dropped_nodes.length > 0 && <>
					<ModalCalendarH5><Translate id='dropped_nodes' />: {tspResponse.dropped_nodes.length}</ModalCalendarH5>
					<ModalCalendarDroppedNodeContainer>
						{tspResponse.dropped_nodes.map(node => {
							const company = (solvedPool ?? companies)[node.node - 1];
							if (!company) return undefined;
							return <DroppedNode key={node.node}>
								<NodeTitle>{props.rawCompanies?.find(c => c.id === company.id)?.name}</NodeTitle>
								{/* TODO use locks to add priority in TSP */}
								{/*<LockButton onClick={() => setLockedCompanies(locked => {
									if (locked.has(node.node)) locked.delete(node.node);
									else locked.add(node.node);
									return new Set(locked);
								})} active={lockedCompanies.has(node.node)}><LockKeyhole /></LockButton>*/}
							</DroppedNode>;
						})}
					</ModalCalendarDroppedNodeContainer>
				</>}
			</>;
			buttons = <>
				<DefaultButton
					margin='0'
					onClick={() => setTspResponse(undefined)}
					buttonStyle={ButtonStyle.White}
				>
					<Translate id='map.modal_right.cancel' />
				</DefaultButton>
				{props.onGenerateEvents && <DefaultButton
					margin='0px'
					buttonStyle={ButtonStyle.IA}
					disabled={!origin || (!isTsp && !destination)}
					onClick={props.onGenerateEvents}
				>
					<Translate id='map.modal_right.generate_route' />
				</DefaultButton>}
				{props.canFillUp && props.plannedEvents.some(e => e.type === EventsTypes.TemporaryEvent) && <div style={{ gridColumn: 'span 2' }}>
					<ModalCalendarP><Translate id='map.modal_right.fill_up_week_description' /></ModalCalendarP>
					<DefaultButton
						margin='0px'
						width='100%'
						buttonStyle={ButtonStyle.White}
						disabled={!origin}
						onClick={() => launchOptimization(undefined, true)}
					>
						<Translate id='map.modal_right.fill_up_week' />
					</DefaultButton>
				</div>}
			</>;

		}
	}

	if (props.noPopup) {
		if (!isOpen) return <></>;
		return <>
			<OptimizeNoPopupContentWrapper gap={gap}>{content}</OptimizeNoPopupContentWrapper>
			<OptimizeNoPopupFooter>{buttons}</OptimizeNoPopupFooter>
		</>;
	}

	return <Popup isOpen={isOpen} onClickOut={() => setIsOpen(false)} popupMode={PopupMode.Details}>
		<OptimizeWrapper>
			<OptimizeNoPopupContentWrapper gap={gap}>{content}</OptimizeNoPopupContentWrapper>
			<OptimizeNoPopupFooter>{buttons}</OptimizeNoPopupFooter>
		</OptimizeWrapper>
	</Popup>;
}

function formatTravelTime(secs: number): string {
	const hours = Math.floor(secs / 3600);
	const minutes = Math.round((secs % 3600) / 60);
	if (hours === 0) return `${minutes}min`;
	return `${hours}h${minutes.toString().padStart(2, '0')}`;
}

function formatTravelKm(km: number): string {
	return `${km < 10 ? km.toFixed(1) : Math.round(km).toString()} km`;
}

const CompareCard = styled.div`
	border: 1px solid ${BorderColor};
	border-radius: 8px;
	padding: 12px;
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 100%;
`;

const CompareCardTitle = styled.div`
	font-weight: 600;
`;

const CompareCardMetrics = styled.div<{ muted?: boolean }>`
	font-size: 12px;
	${p => p.muted ? `color: ${DarkGreySidely2};` : ''}
`;

const TravelSummaryContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	border: 1px solid ${BorderColor};
	border-radius: 8px;
	padding: 10px;
	gap: 4px;
	font-size: 12px;
`;

const TravelSummaryHeader = styled.div`
	display: flex;
	justify-content: space-between;
	color: ${DarkGreySidely2};
	font-size: 11px;
`;

const TravelSummaryRow = styled.div<{ emphasis?: boolean }>`
	display: flex;
	justify-content: space-between;
	gap: 10px;
	${p => p.emphasis ? `font-weight: 600; border-top: 1px solid ${BorderColor}; padding-top: 4px; margin-top: 2px;` : ''}
`;

export const BlueTextA = styled.a<{ noFullWidth?: boolean }>`
	color: ${PrimaryColor} !important;
	font-size: 12px;
	${p => p.noFullWidth ? '' : 'width: 100%;'}
	gap: 5px;
  	display: flex;
   	align-items: center;
    & > svg {
			color: ${PrimaryColor};
	}
`;

export const ModalCalendarSettingsDescriptionAndDropdown = styled.div<{ gap?: string }>`
	display: flex;
	flex-direction: column;
	width: 100%;
	${props => props.gap ? `gap: ${props.gap};` : ''}
`;

export const ModalCalendarH4 = styled.h4<{ isPopup?: boolean }>`
	margin: 0;
	font-size: 13px;
	align-self: flex-start;
	grid-column: span 2;
`;

const ModalCalendarH5 = styled.h5`
	margin: 0;
	font-size: 13px;
	align-self: flex-start;
	width: 100%;
	padding-bottom: 10px;
	margin-bottom: -10px;
`;

export const ModalCalendarDroppedNodeContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	width: 100%;
	gap: 10px;
`;

const DroppedNode = styled.div`
	border: 1px solid ${BorderColor};
	padding: 10px;
	overflow: hidden;
	height: 50px;
		display: flex;
	 	align-items: center;
	  flex-shrink: 0;
	  justify-content: space-between;
	  border-radius: 5px;
`;

export const ModalCalendarP = styled.p`
	margin: 0;
`;

const NodeTitle = styled(ModalCalendarP)`
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`;

const OptimizeNoPopupContentWrapper = styled.div<{ gap?: string }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${({ gap }) => gap ?? '10px'};
	overflow: auto;
	flex-grow: 1;
`;

const OptimizeNoPopupFooter = styled.div`
	gap: 10px;
	flex-shrink: 0;
	display: grid;
	grid-template-columns: 1fr 1fr;

`;

const OPTIMIZATION_RIGHT_PANEL_WIDTH = 343;
export const OPTIMIZATION_RIGHT_PANEL_WIDTH_WITHOUT_PADDING = OPTIMIZATION_RIGHT_PANEL_WIDTH - 23;

function ScheduledEventPopup({ event }: { event: CalendarEvent }) {
	return <>
		<NoMarginH6>{event.companieName}</NoMarginH6>
		<NoMarginP style={{ fontSize: 11, textAlign: 'center' }}>{moment(event.start).format('L')} - {moment(event.start).format('LT')} - {moment(event.end).format('LT')}</NoMarginP>
	</>;
}

export function ModalCalendarContent(props: {
	sortAndDots: SortAndDots,
	onClose: () => void,
	onBack: () => void,
	onEventCreate?: () => void,
	deleteDots: () => void,
	defaultCompanies?: number[],
	allCompanies?: MapDotType[]
}) {
	const profile = useRecoilValue(AProfile);
	const [_, resetPanelCalendar] = React.useContext(PanelContext).calendarViewState.resetHook;
	const [formId, setFormId] = useLocalStorageState<number | undefined>('optimization-default-form', undefined);
	const originHook = React.useState<InputAddressResult | undefined>(initWaypoint('origin', profile));
	const originLatLng = React.useMemo(() => {
		const og = originHook[0];
		if (!og) return undefined;
		return { lat: og.latitude, lng: og.longitude };
	}, [originHook[0]]);

	const canUserAccessFn = canUserAccess();
	const formTemplates = useRecoilValue(AFormTemplate).instanceForms;
	const formTemplatesDropdowm = formTemplates.reduce<DropdownData<number>[]>((acc, t) => {
		if (canUserAccessFn('CreateForm', t.id)) acc.push({ label: t.name, value: t.id });
		return acc;
	}, []);

	const [events, setEvents] = React.useState<CalendarEvent[]>([]);
	const user = useMe();
	const [loadingState, setLoadingState] = React.useState<LoadingState>(null);
	const [hoveredEvent, setHoveredEvent] = React.useState<HoveredEvent>();
	const firstEventDate = new Date();
	// The map follows the calendar: same period, same date. Week = every trip
	// of the week, day = that day's trip, month = every trip of the month.
	const [calendarView, setCalendarView] = React.useState<CalendarPeriod>('week');
	const [calendarDate, setCalendarDate] = React.useState<Date>(new Date());
	const calendarTime = React.useMemo(() => moment(calendarDate), [calendarDate]);
	const tripFilter = useTripFilter(calendarView);
	const [hotelNights, setHotelNights] = React.useState<HotelNightConstraint>();
	const [maxEvent, setMaxEvent] = useLocalStorageState<number | undefined>('planEventMaxEvent', undefined);
	const [maxKm, setMaxKm] = useLocalStorageState<number | undefined>('planEventMaxKm', undefined);
	const [optimizationCompanies, setOptimizationCompanies] = React.useState<OptimizeCompany[]>();
	// Snapshot of the last solve so the user can fine-tune one day at a time
	// from the calendar UI (X to remove a proposed visit, "Replay" per day).
	const [lastSolve, setLastSolve] = React.useState<{
		body: TSPBody
		points: PointConfig[]
		hotelNights: HotelNightConstraint | undefined
		workingDays: WorkingDaysConstraint | undefined
		companies: OptimizeCompany[]
	}>();
	const { loaderNotify } = React.useContext(LoaderContext);
	const mapEvents = React.useMemo(() => events.some(e => e.type === EventsTypes.TemporaryEvent) ? [{
		events: events
			.reduce<MapEventOptions['events']>((acc, event) => {
				if (event.type !== EventsTypes.TemporaryEvent && !event.scheduled)
					return acc;
				// Prefer the event's own coordinates — set when the event was placed
				// on the calendar. Falls back to looking up the company in any
				// available source if the event itself lacks lat/lng.
				// Why this matters: when the user has applied a filter that excludes
				// a planned-event company, the company lookup against
				// `sortAndDots.dots` returns undefined and the event silently dropped
				// out of the route polyline — even though the solver routes through it.
				let lat = event.latitude;
				let lng = event.longitude;
				const company = ((props.sortAndDots.dots ?? props.allCompanies ?? optimizationCompanies) as (MapDotType<number> | OptimizeCompany)[] | undefined)?.find(c => c.id == event.clientCompanyId);
				if ((!lat || !lng) && company) {
					lat = ('latitude' in company ? company.latitude : company.lat) ?? lat;
					lng = ('longitude' in company ? company.longitude : company.lng) ?? lng;
				}
				if (!lat || !lng) return acc;
				// Planned events render as MapPinWithImage('Clock') in EventPin
				// — same pin shape as other events (so hover-blacken and the position
				// badge work as usual) but with the planned-event icon overlay and
				// the event-type colour. mapDots is gated below to NOT add a separate
				// ClockCircle MapDot when EventPin is rendering them, avoiding doubles.
				const isPlanned = event.type !== EventsTypes.TemporaryEvent && event.scheduled;
				const eventTypeColor = isPlanned ? colorTypeMapping[event.eventTypeId - 1]?.color : undefined;
				acc.push({
					companyId: event.clientCompanyId ?? company?.id ?? 0,
					eventId: event.id,
					eventName: event.title,
					lat,
					lng,
					time: moment(event.start),
					companyName: event.companieName ?? company?.name ?? '',
					pinImageString: isPlanned ? 'Clock' : undefined,
					color: eventTypeColor,
				});
				return acc;
			}, [])
	}] : [], [events, props.allCompanies, optimizationCompanies, props.sortAndDots.dots]);
	// A dot in the calendar's day header for every day the map draws a trip on.
	const dayDotColor = React.useCallback((day: Date) =>
		mapEvents.some(me => me.events.some(e => e.time.isSame(day, 'day'))) ? tripFilter.dotColor(day) : undefined, [mapEvents, tripFilter.dotColor]);

	// ---- Drag-from-map → drop-on-calendar (manual fine-tuning) ----
	// Tracks the company currently being dragged from a map dot. Stored in a ref
	// so the per-dot onDragStart closure doesn't force re-renders.
	const draggedCompanyRef = React.useRef<{
		id: number
		lat: number
		lng: number
		name: string
		durationSecs: number
	} | null>(null);

	const findCompanyDuration = React.useCallback((companyId: number): number => {
		if (!lastSolve) return 30 * 60;
		const pt = lastSolve.body.points.find(p => p.id === companyId);
		if (pt?.node_duration != null) return pt.node_duration;
		return lastSolve.body.visit_duration || 30 * 60;
	}, [lastSolve]);

	const handleDotDragStart = React.useCallback((dot: MapDotType<number | string>, e: React.DragEvent) => {
		if (!lastSolve) return;
		if (typeof dot.id !== 'number') return;
		draggedCompanyRef.current = {
			id: dot.id,
			lat: dot.latitude,
			lng: dot.longitude,
			name: dot.name,
			durationSecs: findCompanyDuration(dot.id),
		};
		e.dataTransfer.effectAllowed = 'copy';
		// Browsers need at least one data type set for the drag to register.
		e.dataTransfer.setData('text/plain', dot.name);
	}, [lastSolve, findCompanyDuration]);

	const handleDotDragEnd = React.useCallback(() => {
		draggedCompanyRef.current = null;
	}, []);

	// rbc calls this during drag-over to size the highlighted slot AND to render
	// the drag-over placeholder through the calendar's event renderer/style getter.
	// We MUST include `type` and `eventTypeId` so the style getter can find a
	// colour entry in colorTypeMapping — otherwise the calendar crashes mid-drag.
	const dragFromOutsideItem = React.useCallback(() => {
		const c = draggedCompanyRef.current;
		const ms = (c?.durationSecs ?? 30 * 60) * 1000;
		return {
			start: new Date(0),
			end: new Date(ms),
			type: EventsTypes.TemporaryEvent,
			eventTypeId: 4,
			title: c?.name ?? '',
		};
	}, []);

	const onDropFromOutside = React.useCallback(({ start }: { start: Date | string, end: Date | string, allDay: boolean }) => {
		const c = draggedCompanyRef.current;
		if (!c) return;
		const startDate = start instanceof Date ? start : new Date(start);
		const endDate = new Date(startDate.getTime() + c.durationSecs * 1000);
		const visitName = translateToString('event.Visit');
		const newEvent: CalendarEvent = {
			type: EventsTypes.TemporaryEvent,
			id: `temporaryEvent[${c.id}]`,
			eventStatusId: 1,
			eventTypeId: 4,
			title: `${visitName} -- ${c.name}`,
			companieName: c.name,
			clientCompanyId: c.id,
			incharge: user.id,
			start: startDate,
			end: endDate,
			latitude: c.lat,
			longitude: c.lng,
			allDay: false,
			draggedIn: true,
		};
		setEvents([...events, newEvent]);
		draggedCompanyRef.current = null;
	}, [events, user.id]);

	const mapDots = React.useMemo(() => {
		// After a solve, restrict the candidate dots to the actual solver pool —
		// the company set the optimization was given. This prevents the map from
		// showing dots that were filtered out / not considered, which clutters
		// the post-optimization route review.
		// Before a solve, fall back to the (possibly filtered) listing pool.
		const baseDots: MapDotType<number | string>[] | undefined = lastSolve
			? lastSolve.companies.map(c => ({
				id: c.id,
				latitude: c.lat,
				longitude: c.lng,
				name: c.name ?? '',
			}))
			: props.sortAndDots.dots ?? props.allCompanies;
		const enableDrag = !!lastSolve;
		const decorate = (dot: MapDotType<number | string>): MapDotType<number | string> =>
			(!enableDrag || typeof dot.id !== 'number')
				? dot
				: { ...dot, draggable: true, onDragStart: e => handleDotDragStart(dot, e), onDragEnd: handleDotDragEnd };

		if (events.length === 0) return baseDots?.map(decorate);
		const eventSet = new Set(events.map(e => e.clientCompanyId));
		const hasTspEvents = events.some(e => e.type === EventsTypes.TemporaryEvent);
		const dots: MapDotType<number | string>[] = baseDots?.reduce<MapDotType<number | string>[]>((acc, dot) => {
			if (eventSet.has(dot.id as number)) return acc;
			acc.push(decorate(dot));
			return acc;
		}, []) ?? [];
		// Surface planned-event dots on the map so they're visible regardless of
		// the company filter — UNLESS the optimization has already produced
		// proposals, in which case EventPin renders the planned events (with the
		// ClockCircle overlay) as part of the route. Adding the dot here too
		// would double up the icon at the same coordinate.
		if (!hasTspEvents) {
			for (const event of events) {
				if (event.type !== EventsTypes.TemporaryEvent && event.latitude && event.longitude && event.scheduled) {
					dots.push({
						time: event.start,
						latitude: event.latitude,
						longitude: event.longitude,
						id: event.id,
						name: event.companieName ?? '',
						childrenWithColor: (color) => <MapPinWithImage imageString='Clock' pinColor={color ?? colorTypeMapping[event.eventTypeId - 1].color} pinSize={MAP_DEFAULT_PIN_SIZE} />,
						popup: <ScheduledEventPopup event={event} />,
					});
				}
			}
		}

		return dots;
	}, [props.sortAndDots, events, props.allCompanies, lastSolve, handleDotDragStart, handleDotDragEnd]);

	// Convert a TSP response into the final `events` list (real events + new
	// temporary events). Shared between the initial solve and the "Replay day"
	// flow below.
	const applyTSPResponse = (
		tspResponse: TSPResponse,
		points: PointConfig[],
		hotelNights: HotelNightConstraint | undefined,
		workingDays: WorkingDaysConstraint | undefined,
		optimizationCompanies: OptimizeCompany[],
	) => {
		const visitName = translateToString('event.Visit');
		const firstDate = moment(workingDays?.startDate ?? firstEventDate);
		const workDays = workingDays!.workdays;
		const finalEvents: CalendarEvent[] = events.filter(e => e.type === EventsTypes.Event);
		for (const route of tspResponse.routes) {
			for (const stop of route.stops) {
				if (stop.arrival === stop.departure) continue;
				const companyId = points[stop.node]?.id;
				if (!companyId) continue;
				const company = optimizationCompanies.find(c => companyId === c.id);
				if (!company) continue;

				const date = firstDate.clone().add(stop.day, 'days');
				let day = date.day() - 1;
				if (day === -1) day = 6;
				while (!workDays.includes(day)) {
					date.add(1, 'day');
					firstDate.add(1, 'day');
					day = date.day() - 1;
					if (day === -1) day = 6;
				}
				const arrival = moment(stop.arrival, 'HH:mm', true);
				const departure = moment(stop.departure, 'HH:mm', true);
				const departureDate = date.set({ hour: departure.hour(), minute: departure.minute() });
				const arrivalDate = date.clone().set({ hour: arrival.hour(), minute: arrival.minute() });
				finalEvents.push({
					type: EventsTypes.TemporaryEvent,
					id: `temporaryEvent[${companyId}]`,
					eventStatusId: 1,
					eventTypeId: 4,
					title: `${visitName} -- ${company.name ?? ''}`,
					companieName: company.name,
					clientCompanyId: companyId,
					incharge: user.id,
					start: arrivalDate.toDate(),
					end: departureDate.toDate(),
					latitude: company.lat,
					longitude: company.lng,
					allDay: false
				});
			}
		}
		setOptimizationCompanies(optimizationCompanies);
		setHotelNights(hotelNights);
		setEvents(finalEvents);
		// Land the calendar (and the map with it) on the first planned day.
		const firstStart = finalEvents.reduce<Date | undefined>((acc, e) =>
			e.type === EventsTypes.TemporaryEvent && (acc === undefined || e.start < acc) ? e.start : acc, undefined);
		if (firstStart) setCalendarDate(firstStart);
	};

	const removeTemporaryEvent = (event: CalendarEvent) => {
		setEvents(events.filter(e => e.id !== event.id));
	};

	// Replace TemporaryEvents on a single day, leaving other days untouched.
	const applyTSPResponseForDay = (
		tspResponse: TSPResponse,
		points: PointConfig[],
		companies: OptimizeCompany[],
		targetDate: Date,
	) => {
		const targetIso = jsDateToNaiveDate(targetDate);
		const preserved = events.filter(e => {
			if (e.type !== EventsTypes.TemporaryEvent) return true;
			return jsDateToNaiveDate(e.start) !== targetIso;
		});
		const visitName = translateToString('event.Visit');
		const newTempEvents: CalendarEvent[] = [];
		const dateMoment = moment(targetDate);
		for (const route of tspResponse.routes) {
			for (const stop of route.stops) {
				if (stop.arrival === stop.departure) continue;
				const companyId = points[stop.node]?.id;
				if (!companyId) continue;
				const company = companies.find(c => companyId === c.id);
				if (!company) continue;
				const arrival = moment(stop.arrival, 'HH:mm', true);
				const departure = moment(stop.departure, 'HH:mm', true);
				const arrivalDate = dateMoment.clone().set({ hour: arrival.hour(), minute: arrival.minute() });
				const departureDate = dateMoment.clone().set({ hour: departure.hour(), minute: departure.minute() });
				newTempEvents.push({
					type: EventsTypes.TemporaryEvent,
					id: `temporaryEvent[${companyId}]`,
					eventStatusId: 1,
					eventTypeId: 4,
					title: `${visitName} -- ${company.name ?? ''}`,
					companieName: company.name,
					clientCompanyId: companyId,
					incharge: user.id,
					start: arrivalDate.toDate(),
					end: departureDate.toDate(),
					latitude: company.lat,
					longitude: company.lng,
					allDay: false,
				});
			}
		}
		setEvents([...preserved, ...newTempEvents]);
	};

	// Replay a single day.
	//
	// A replay re-sequences exactly what is on the calendar for that day, plus
	// the companies the user dragged in — nothing else:
	//
	//   pool  = origin + kept planned visits + dragged-in companies
	//   locks = original LVs + kept planned visits (order and slots hold)
	//   dragged-in = unlocked mandatory jobs the solver places freely
	//
	// The day's end is extended to late evening: an addition lengthens the
	// route instead of being rejected against work_end, and the original
	// solve's shaping constraints (opening hours, caps) don't apply to a
	// manual tune.
	//
	// Why a one-day solve instead of re-solving the whole week:
	//   - Smaller model, finishes well under the adaptive time budget.
	//   - Tight locks across 5+ days are easily infeasible if travel-time
	//     re-computation differs slightly; the solver then returns an empty
	//     response and the calendar gets wiped. One day's worth of locks is robust.
	const replayDay = (date: Date) => {
		if (!lastSolve) return;
		const targetIso = jsDateToNaiveDate(date);
		// JS getDay(): Sun=0..Sat=6. WEEK_DAY_LIST: Mon=0..Sun=6.
		const appDayIdx = (date.getDay() + 6) % 7;
		const weekday = WEEK_DAY_LIST[appDayIdx];

		// --- 1. This day's events: kept planned visits vs dragged-in ones. ---
		const targetTemporaryEvents = events.filter(e =>
			e.type === EventsTypes.TemporaryEvent && jsDateToNaiveDate(e.start) === targetIso
		);
		const keptEvents = targetTemporaryEvents.filter(e => !e.draggedIn);
		const addedCompanyIds = new Set<number>();
		for (const e of targetTemporaryEvents) {
			if (e.draggedIn && e.clientCompanyId != null) addedCompanyIds.add(e.clientCompanyId);
		}

		// --- 2. Restricted pool: origin + this day's companies, nothing else.
		// No substitution — and with a pool this small the server-side
		// candidate pre-filter passes everything through untouched.
		const targetLvNodes = new Set(
			lastSolve.body.locked_visits.filter(lv => lv.day === targetIso).map(lv => lv.node_index)
		);
		const involved = new Set<number>(addedCompanyIds);
		for (const e of keptEvents) {
			if (e.clientCompanyId != null) involved.add(e.clientCompanyId);
		}
		const newPoints: PointConfig[] = [];
		const oldToNew = new Map<number, number>();
		for (let i = 0; i < lastSolve.body.points.length; i++) {
			const p = lastSolve.body.points[i];
			const keep = p.id == null || targetLvNodes.has(i) || (p.id != null && involved.has(p.id));
			if (!keep) continue;
			oldToNew.set(i, newPoints.length);
			// dragged-in companies are mandatory yet freely placeable
			newPoints.push(p.id != null && addedCompanyIds.has(p.id) ? { ...p, priority: LOCKED_VISIT_PRIORITY } : p);
		}

		// --- 3. Re-index original LVs that fall on the target day. ---
		const targetOriginalLVs = lastSolve.body.locked_visits
			.filter(lv => lv.day === targetIso)
			.map(lv => {
				const newIdx = oldToNew.get(lv.node_index);
				return newIdx === undefined ? null : { ...lv, node_index: newIdx };
			})
			.filter((lv): lv is LockedVisit => lv !== null);

		// --- 4. Locks for KEPT planned visits only: their order and slots
		// hold. Dragged-in visits stay unlocked so the solver sequences them
		// wherever they fit best between the locks — or after the last one,
		// since the day end is extended.
		const derivedLocks = eventsToLockedVisits(
			keptEvents as CalendarEvent[],
			newPoints,
			jsDateToNaiveDate,
			secondsSinceMidnight,
		);

		const newBody: TSPBody = {
			...lastSolve.body,
			points: newPoints,
			first_visit_day: targetIso,
			max_days: 1,
			working_days: [weekday],
			first_visit_hour: lastSolve.body.working_hours.start,
			hotel_nights: {},
			all_nights_in_hotels: false,
			locked_visits: [...targetOriginalLVs, ...derivedLocks],
			// the end-of-day constraint does not apply to a manual tune
			working_hours: { start: lastSolve.body.working_hours.start, end: REPLAY_EXTENDED_DAY_END },
			openning_hours_field_id: [],
			max_visits_per_day: null,
			advanced_settings: { ...lastSolve.body.advanced_settings, max_daily_km: undefined },
		};

		loaderNotify('global', LoadingStateEnum.LOADING);
		sequenceEventsTSP(newBody)
			.then(res => {
				loaderNotify('global', LoadingStateEnum.LOADED);
				if (res.routes.length === 0) {
					toast.error(translateToString('tsp_error_no_solution_day'));
					return;
				}
				// IMPORTANT: pass `newPoints` (not lastSolve.body.points). The response's
				// `stop.node` indices reference newPoints.
				applyTSPResponseForDay(res, newPoints, lastSolve.companies, date);
				// Intentionally NOT updating lastSolve.body — the original full-week
				// body remains the source of truth so further replays on other days
				// still work from the original constraints.

				// Kept visits the solver could not keep: say so instead of letting
				// them silently vanish from the day (they stay available as map
				// dots). Locks on id-less points (planned events) are excluded —
				// their real calendar events are preserved regardless.
				const servedNodes = new Set(res.routes.flatMap(r => r.stops.map(s => s.node)));
				const servedIds = new Set(res.routes.flatMap(r => r.stops.map(s => newPoints[s.node]?.id)).filter(id => id != null));
				const lost = newBody.locked_visits.filter(lv => !servedNodes.has(lv.node_index) && newPoints[lv.node_index]?.id != null);
				const lostAddedIds = [...addedCompanyIds].filter(id => !servedIds.has(id));
				if (lost.length + lostAddedIds.length > 0) {
					const names = [
						...lost.map(lv => lastSolve.companies.find(c => c.id === newPoints[lv.node_index]?.id)?.name),
						...lostAddedIds.map(id => lastSolve.companies.find(c => c.id === id)?.name),
					].filter((n): n is string => !!n);
					toast.warn(translateToString('tsp_visits_not_placed', [['count', String(lost.length + lostAddedIds.length)], ['names', `${names.slice(0, 4).join(', ')}${names.length > 4 ? '…' : ''}`]]));
				}
			})
			.catch(e => {
				console.error(e);
				toast.error(translateToString('tsp_error_replay_failed'));
				loaderNotify('global', LoadingStateEnum.LOADED);
			});
	};

	return <FlexDiv width='100%' height={'100%'}>
		<div style={{ height: '100%', width: `calc(100% - ${OPTIMIZATION_RIGHT_PANEL_WIDTH}px)`, transition: 'width 0.3s' }}>
			{/* @ts-expect-error I assure, this is working */}
			<Splitter
				style={{ height: '100%', width: '100%' }}
				direction="vertical"
				sizes={[66, 34]}
				minSize={0}
			>
				<div style={{ overflow: 'auto' }}>
					<SidelyCalendar
						setEvents={(events) => setEvents(events as CalendarEvent[])}
						events={events}
						setLoadingState={setLoadingState}
						loadingState={loadingState}
						style={{ width: 'auto', margin: 'auto' }}
						restricted
						date={calendarDate}
						onDateChange={setCalendarDate}
						onViewChange={setCalendarView}
						dayDotColor={dayDotColor}
						onDayDotClick={tripFilter.toggle}
						onEventHover={event => {
							if (!event || !('id' in event)) return setHoveredEvent(undefined);
							let companyId: number | undefined;
							if ('clientCompanyId' in event) companyId = event.clientCompanyId;
							else if ('companyId' in event) companyId = event.companyId;
							setHoveredEvent({ id: event.id.toString(), companyId, fromCalendar: true });
						}}
						hoveredEvent={hoveredEvent}
						onRemoveTemporaryEvent={lastSolve ? removeTemporaryEvent : undefined}
						onReplayDay={lastSolve ? replayDay : undefined}
						onDropFromOutside={lastSolve ? onDropFromOutside : undefined}
						dragFromOutsideItem={lastSolve ? dragFromOutsideItem : undefined}
					/>
					<ComponentLoader
						loaderId='loader-optimization'
						height='100%'
						top='15px'
					/>
				</div>
				<div style={{ overflow: 'hidden' }}>
					<CalendarMapView
						granularity={calendarView}
						time={calendarTime}
						hiddenTrips={tripFilter.hidden}
						onToggleTrip={tripFilter.toggle}
						origin={originLatLng}
						mapEvents={mapEvents}
						drawRoute
						useGranularity
						hoveredEvent={hoveredEvent}
						setHoveredEvent={setHoveredEvent}
						height='100%'
						dots={mapDots}
						hotelNights={hotelNights ?? { allNights: false, nights: [] }}
					/>
				</div>
			</Splitter>
		</div>
		<OptimizeRightPanel isOpen={true}>
			<NoMarginH5 style={{ height: '50px', display: 'flex', alignItems: 'center', fontSize: '13px' }}><Translate id='route_parameters' /></NoMarginH5>
			<OptimizePopup
				originHook={originHook}
				alwaysTsp
				mainPageConstraints={new Set(['VisitDuration', 'WorkingDays'])}
				defaultConstraints={new Set(['PlanifiedEvents'])}
				samePriority={!props.sortAndDots.sort}
				rawCompanies={optimizationCompanies ?? props.sortAndDots.dots ?? props.allCompanies}
				companies={props.sortAndDots.dots?.map(c => ({ id: c.id, lat: c.latitude, lng: c.longitude, name: c.name })) ?? optimizationCompanies ?? props.allCompanies?.map(c => ({ id: c.id, lat: c.latitude, lng: c.longitude, name: c.name }))}
				canFillUp
				errorCompanies={[]}
				onOptimize={console.log}
				isOpen
				setIsOpen={props.onClose}
				pauseDuration={3600000}
				firstEventDate={firstEventDate}
				noPopup
				allInformations
				plannedEvents={events}
				onGenerateEvents={() => {
					const filteredEvents = events.filter(e => e.type == EventsTypes.TemporaryEvent);
					if (!filteredEvents.length) return;
					const body = {
						events: filteredEvents.map(e => ({
							title: e.title,
							start: ~~(+e?.start / 1000),
							end: ~~(+e?.end / 1000),
							company_id: e.clientCompanyId
						})),
						linked_form: formId,
						type_id: filteredEvents[0].eventTypeId,
						owner_id: user.id
					};
					bulkCreateEvents(body).catch(console.error).then(() => {
						props.onEventCreate?.();
						resetPanelCalendar(r => !r);
					});
					events.filter(e => e.temporaryModified).forEach(modifyEvent);
					props.onClose();
				}}
				maxVisitsPerDay={maxEvent}
				maxDailyKm={maxKm}
				onTSPOptimize={(tspResponse, points, hotelNights, workingDays, optimizationCompanies, body) => {
					applyTSPResponse(tspResponse, points, hotelNights, workingDays, optimizationCompanies);
					setLastSolve({ body, points, hotelNights, workingDays, companies: optimizationCompanies });
				}}
			>
				<ModalCalendarH4><Translate id='companies' /> ({props.sortAndDots.dots?.length ?? props.allCompanies?.length})</ModalCalendarH4>
				{props.sortAndDots.dots && props.sortAndDots.dots?.length > 0 ? <>
					<ModalCalendarSettingsDescriptionAndDropdown gap='5px'>
						<DeletePauseDiv hoverColor={OrangeSidely} onClick={props.onBack}>
							<RefreshCw size={16} />
							<Translate id='edit_sorts' />
						</DeletePauseDiv>
						<DeletePauseDiv onClick={props.deleteDots}>
							<Trash size={16} />
							<Translate id='delete_sorts' />
						</DeletePauseDiv>
					</ModalCalendarSettingsDescriptionAndDropdown>
				</> : <>
					<BlueTextA onClick={props.onBack}><Translate id='filter_and_order_companies' /></BlueTextA>
				</>}
				<Dropdown
					cancellable
					datalist={formTemplatesDropdowm}
					selectedValue={formTemplatesDropdowm.find(t => t.value == formId)}
					onChange={value => setFormId(value?.value)}
					name='optimization-form-template'
					dropdownStyle={{ width: '100%', labelUp: true, optionWidth: `${OPTIMIZATION_RIGHT_PANEL_WIDTH_WITHOUT_PADDING}px`, height: '35px', containerWidth: '100%', optionHeight: '200px', containerHeight: 'fit-content', }}
					label={translateToString('form')}
				/>
				<Input
					label={translateToString('map.modal_right.max_events_per_day')}
					type='number'
					min={1}
					value={maxEvent}
					onChange={e => {
						const count = parseInt(e);
						setMaxEvent(count > 0 ? count : undefined);
					}}
					placeholder={'Max'}
					name={''}
					inputStyle={{ width: '100%', labelUp: true, containerWidth: '100%', containerHeight: 'fit-content', }}
				/>
				<Input
					label={translateToString('tsp_max_daily_km')}
					type='number'
					min={1}
					value={maxKm}
					onChange={e => {
						const km = parseFloat(e);
						setMaxKm(km > 0 ? km : undefined);
					}}
					placeholder={'Max'}
					name={''}
					inputStyle={{ width: '100%', labelUp: true, containerWidth: '100%', containerHeight: 'fit-content', }}
				/>
			</OptimizePopup>
		</OptimizeRightPanel>
	</FlexDiv>;

}

export default function ModalCalendar(props: {
	isOpen: boolean
	toggleModal: () => void
	events: unknown[]
	companies: any[]
	name: string
	form_id?: number
	onClose: () => void
	id: number
	isVisit: boolean
	onOptimize: (ids: string[], interconnections: Interconnection[]) => void
	v2?: boolean,
	onEventCreate?: () => void,
	hourStartDay?: Date,
	hourEndDay?: Date,
	pauseHour?: Date,
	visitDuration?: Duration,
	pauseDuration?: number,
	daysSelected?: number[],
	firstEventDate?: Date,
	firstEventHour?: number,
	onTSPOptimize?: (res: TSPResponse, points: PointConfig[], hotelNights: HotelNightConstraint | undefined, workingDays: WorkingDaysConstraint | undefined, additionalCompanies: OptimizeCompany[], body: TSPBody) => void,
	hotelNights?: HotelNightConstraint,
	maxVisitsPerDay?: number
}) {
	const translate = getTranslate(storeLang.getState().localize);
	const [loadingState, setLoadingState] = React.useState<LoadingState>(null);
	const [events, setEvents] = React.useState<any[]>(toTemporaryEvents(props.events));
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [optimizeOpen, setOptimizeOpen] = React.useState<boolean>(false);
	const [hoveredEvent, setHoveredEvent] = React.useState<HoveredEvent>();
	const { height, width } = useWindowDimensions();
	// The map follows the calendar: same period, same date. Week = every trip
	// of the week, day = that day's trip, month = every trip of the month.
	const [calendarView, setCalendarView] = React.useState<CalendarPeriod>('week');
	const [calendarDate, setCalendarDate] = React.useState<Date>(() => events[0]?.start ? new Date(events[0].start) : new Date());
	const calendarTime = React.useMemo(() => moment(calendarDate), [calendarDate]);
	const tripFilter = useTripFilter(calendarView);
	const profile = useRecoilValue(AProfile);
	const originHook = React.useState<InputAddressResult | undefined>(initWaypoint('origin', profile));
	const originLatLng = React.useMemo(() => {
		const og = originHook[0];
		if (!og) return undefined;
		return { lat: og.latitude, lng: og.longitude };
	}, [originHook[0]]);
	const mapEvents = React.useMemo(() => {
		return [{
			events: events
				.filter(event => event.type == EventsTypes.TemporaryEvent)
				.map(event => {
					const company = props.companies.find(c => (c.id ?? c.company_id) == event.clientCompanyId) ?? {};
					return {
						companyId: company.id ?? company.company_id,
						eventId: event.id,
						eventName: event.name,
						lat: company.lat,
						lng: company.lon,
						time: moment(event.start ?? event.startDate),
						companyName: company.name
					};
				})
				.filter(e => e.lat && e.lng)
		}];
	}, [events]);
	// A dot in the calendar's day header for every day the map draws a trip on.
	const dayDotColor = React.useCallback((day: Date) =>
		mapEvents.some(me => me.events.some(e => e.time.isSame(day, 'day'))) ? tripFilter.dotColor(day) : undefined, [mapEvents, tripFilter.dotColor]);

	const plannedEvents: PlannedEvent[] = React.useMemo(() => events
		.filter(event => event.type == EventsTypes.Event)
		.map((event: CalendarEvent) => ({
			...event,
			isInOptimization: !!props.companies.find(c => (c.id ?? c.company_id) == event.clientCompanyId)
		})), [events]);

	React.useEffect(() => {
		if (props.events.length > 0 && props.isOpen) {
			const temporaryEvents = toTemporaryEvents(props.events);
			setEvents([
				...temporaryEvents,
				...events.filter(event => event.type !== EventsTypes.TemporaryEvent)
			]);
			// Land the calendar (and the map with it) on the first planned day.
			if (temporaryEvents[0]?.start) setCalendarDate(new Date(temporaryEvents[0].start));
			setIsOpen(props.isOpen);
		}
	}, [props.events, props.isOpen]);

	const isAdmin = isSuperAdmin();

	return (
		<div>
			<Modal
				size="lg"
				style={{ maxWidth: width - 200, width: width - 200 }}
				isOpen={isOpen}
				toggle={() => {
					props.toggleModal();
					setIsOpen(!isOpen);
				}}
				centered
			>
				<ModalNoClose>
					<ModalHeader
						toggle={() => {
							props.toggleModal();
							setIsOpen(!isOpen);
						}}
					>
						<TopContainer width={width - 230}>
							<Translate id="map.modal_right.preview_your_events" />
							<ButtonContainer>
								<Loader width='20px' id='loader-optimization' />
								{props.isVisit && !optimizeOpen && <DefaultButton
									height='42px'
									width='160px'
									buttonStyle={ButtonStyle.IA}
									disabled={props.companies?.filter(c => c.lat && c.lon).length > (isAdmin ? MAX_NEW_ALGORITHM : MAX_OLD_ALGORITHM) || props.companies?.filter(c => c.lat && c.lon).length <= 1}
									onClick={() => setOptimizeOpen(true)}
								>
									<img src={OptimizeIMG} style={{ position: 'relative', right: '2px', bottom: '1px' }} />
									<Translate id='map.modal_right.optimize' />
								</DefaultButton>
								}
								<DefaultButton
									height='42px'
									width='130px'
									buttonStyle={ButtonStyle.White}
									onClick={() => {
										props.toggleModal();
										setIsOpen(!isOpen);
									}}
								>
									<Translate id='map.modal_right.cancel' />
								</DefaultButton>
								<DefaultButton
									height='42px'
									width='130px'
									buttonStyle={ButtonStyle.Default}
									onClick={() => {
										const filteredEvents = events.filter(e => e.type == EventsTypes.TemporaryEvent);
										if (!filteredEvents.length) return;
										const body = {
											events: filteredEvents.map(e => ({
												title: e.title,
												start: ~~(+e?.start / 1000),
												end: ~~(+e?.end / 1000),
												company_id: parseInt(e.clientCompanyId),
												description: e.description
											})),
											linked_form: props.form_id,
											type_id: filteredEvents[0].eventTypeId,
											owner_id: parseInt(filteredEvents[0].incharge)
										};
										if (!props.v2) {
											toast.promise(
												bulkCreateEvents(body).catch(console.error),
												{
													pending: `${translate('map.modal_right.creation_of')} ${filteredEvents.length} ${props.name}${props.name.toLowerCase().slice(-1) === 's' ? '' : 's'}`,
													success: `${translate('map.modal_right.events_created')}`,
													error: `${translate('map.modal_right.error_creating_events')}`
												}
											).then(props.onEventCreate);
										} else {
											bulkCreateEvents(body).catch(console.error).then(props.onEventCreate);
										}
										events.filter(e => e.temporaryModified).forEach(modifyEvent);
										props.onClose();
										props.toggleModal();
										setIsOpen(!isOpen);
									}}
								>
									<Translate id='map.modal_right.generate' />
								</DefaultButton>
							</ButtonContainer>
						</TopContainer>
					</ModalHeader>
					<ModalBody style={{ display: 'flex' }}>
						{/* @ts-expect-error I assure, this is working */}
						<Splitter
							style={{ height: height - 200, width: `calc(100% - ${optimizeOpen ? OPTIMIZATION_RIGHT_PANEL_WIDTH : 0}px)`, transition: 'width 0.3s' }}
							direction="vertical"
							sizes={props.isVisit && isOpen ? [66, 34] : [100]}
							ishidden={(!(props.isVisit && isOpen)).toString()}
							minSize={0}
						>
							<div style={{ overflow: 'auto', height: props.isVisit && isOpen ? undefined : '100%' }}>
								<SidelyCalendar
									setEvents={setEvents}
									events={events}
									setLoadingState={setLoadingState}
									loadingState={loadingState}
									style={{ width: 'auto', margin: 'auto' }}
									restricted
									id={props.id}
									date={calendarDate}
									onDateChange={setCalendarDate}
									onViewChange={setCalendarView}
									dayDotColor={dayDotColor}
									onDayDotClick={tripFilter.toggle}
									onEventHover={event => {
										if (!event || !('id' in event)) return setHoveredEvent(undefined);
										let companyId: number | undefined;
										if ('clientCompanyId' in event) companyId = event.clientCompanyId;
										else if ('companyId' in event) companyId = event.companyId;
										setHoveredEvent({ id: event.id.toString(), companyId, fromCalendar: true });
									}}
									hoveredEvent={hoveredEvent}
								/>
								<ComponentLoader
									loaderId='loader-optimization'
									height='100%'
									top='15px'
								/>
							</div>
							<div style={{ height: props.isVisit && isOpen ? undefined : 0, overflow: 'hidden' }}>
								{props.isVisit && isOpen &&
									<CalendarMapView
										granularity={calendarView}
										time={calendarTime}
										hiddenTrips={tripFilter.hidden}
										onToggleTrip={tripFilter.toggle}
										origin={originLatLng}
										mapEvents={mapEvents}
										drawRoute
										useGranularity
										hoveredEvent={hoveredEvent}
										setHoveredEvent={setHoveredEvent}
										height='100%'
										width={width - 232}
										hotelNights={props.hotelNights}
									/>
								}
							</div>
						</Splitter>
						<OptimizeRightPanel isOpen={optimizeOpen} height={height - 200}>
							<OptimizePopup
								originHook={originHook}
								rawCompanies={props.companies}
								companies={props.companies.filter(c => c.lat && c.lon).map(c => ({ id: c.id, lat: c.lat, lng: c.lon }))}
								errorCompanies={props.companies.filter(c => !c.lat || !c.lon)}
								onTSPOptimize={props.onTSPOptimize}
								onOptimize={props.onOptimize}
								isOpen={optimizeOpen}
								setIsOpen={setOptimizeOpen}
								hourStartDay={props.hourStartDay}
								hourEndDay={props.hourEndDay}
								pauseHour={props.pauseHour}
								visitDuration={props.visitDuration}
								pauseDuration={props.pauseDuration}
								daysSelected={props.daysSelected}
								firstEventDate={props.firstEventDate}
								noPopup
								plannedEvents={plannedEvents}
							/>
						</OptimizeRightPanel>
					</ModalBody>
				</ModalNoClose>
			</Modal>
		</div>
	);
}

const OptimizeWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 10px;
	padding: 15px;
	height: 100%;
`;

const OptimizeRightPanel = styled(OptimizeWrapper) <{ isOpen: boolean, height?: number }>`
	width: ${p => p.isOpen ? OPTIMIZATION_RIGHT_PANEL_WIDTH : 0}px;
	height: ${p => p.height ? `${p.height}px` : '100%'};
	transition: width 0.3s;
	border-left: ${p => p.isOpen ? `1px solid ${BorderColor}` : 'none'};
	padding: 10px;
	padding-right: 0;
	margin-left: ${p => p.isOpen ? 10 : 0}px;
	overflow: hidden;
	flex-shrink: 0;
`;

const ComponentLoaderBackground = styled.div<{ allScreen?: boolean, zIndex?: string, height?: string, width?: string, top?: string, left?: string, isLoading?: boolean, noBackground?: boolean, noMarginLeft?: boolean }>`
	width: ${p => p.width ?? '100%'};
	height: ${p => p.height ?? '100%'};
	${p => p.noBackground ? '' : `background-color: color-mix(in oklab, ${SidelyBlack} 40%, transparent);`}
	position: absolute;
	top: ${p => p.top ?? '0'};
	left: ${p => p.left ?? '0'};
	display: ${p => p.isLoading ? 'flex' : 'none'};
	align-items: center;
	justify-content: center;
	${p => p.zIndex ? `z-index: ${p.zIndex};` : ''}
	${p => p.allScreen
		? `
		width: 100vw;
		height: 100vh;
		z-index: 99999;
		${p.noMarginLeft ? '' : `margin-left: -${SIDEBAR_CLOSE_WIDTH}px;`}
	`
		: ''}
`;

export const RoundProgressBar = styled.div<{ value: number, size: number, background?: string, borderSize?: number }>`
	flex-shrink: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${p => p.size}px;
	height: ${p => p.size}px;
	border-radius: 50%;
	background:
	  radial-gradient(closest-side, ${p => p.background ?? SurfaceColor} calc(100% - ${p => p.borderSize ?? 2}px), transparent calc(100% - ${p => p.borderSize ?? 2}px) 100%),
	  conic-gradient(${PrimaryColor} ${p => p.value * 100}%, ${p => p.background ?? SurfaceColor} ${p => p.value * 100}%);
`;

export function ComponentLoader(props: {
	loaderId?: string
	height?: string
	top?: string
	left?: string
	loadingState?: LoadingStateEnum
	zIndex?: string
	allScreen?: boolean
	noBackground?: boolean
	noMarginLeft?: boolean
	loaderState?: LoaderState
}) {
	const { loaderId, height, top, left, allScreen, noBackground } = props;
	const { getLoaderStatus } = React.useContext(LoaderContext);
	// @ts-expect-error state
	const state: LoaderState | undefined = props.loaderState ?? getLoaderStatus(loaderId ?? '') ?? useSelector(state => state.webSocket);

	const status = props.loadingState ?? state?.status;
	const isLoading = status == LoadingStateEnum.LOADING;

	return <ComponentLoaderBackground
		height={height}
		top={top}
		left={left}
		isLoading={isLoading}
		zIndex={props.zIndex}
		allScreen={allScreen}
		noBackground={noBackground}
		noMarginLeft={props.noMarginLeft}
		id={loaderId === 'global' ? 'global-loader' : undefined}
	>
		<UserDoodle loading size={60} />
	</ComponentLoaderBackground>;
}
